import { ButtonBorderless } from "@nova/component-btn"
import { Checkbox } from "@nova/component-checkbox"
import { AutoPerimeterEntity } from "@nova/constants"
import classNames from "classnames"
import { InspectorPanelAccordion } from "features/map/inspector-panel/accordion/InspectorPanelAccordion"
import { noop } from "nodash"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { useCallback, useState } from "react"

export const FirePerimeterPluginActionMethodAutoAccordion = ({
    title,
    entities,
    onEntityClicked,
    isOpen,
    setIsOpen,
    className,
}: {
    title: string
    entities: AutoPerimeterEntity[]
    onEntityClicked: (entity: AutoPerimeterEntity, isSelected: boolean) => void
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    className?: string
}) => {
    const [idsChecked, setIdsChecked] = useState(new Set<string>())

    const handleSelectAll = useCallback(() => {
        let isSelected: boolean
        // Deselect all
        if (idsChecked.size === entities.length) {
            setIdsChecked(new Set())
            isSelected = false
        }
        // Select all, except those with an errorMessage.
        else {
            setIdsChecked(
                new Set(
                    entities.reduce((acc, cur) => {
                        if (!cur.errorMessage) acc.push(cur.id)
                        return acc
                    }, [] as string[])
                )
            )
            isSelected = true
        }

        entities.forEach((entity) => {
            if (!entity.errorMessage) onEntityClicked(entity, isSelected)
        })
    }, [idsChecked, entities, onEntityClicked])

    return (
        <div className="-mx-4 flex flex-col">
            <InspectorPanelAccordion
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                title={title}
                shouldPadChildrenVertically={false}
                extraVerticalPaddingInRem={0.5}
                hasSubsections={true}
                className={className}
            >
                <div className="mx-4 flex flex-col items-start">
                    <ButtonBorderless
                        title={
                            (idsChecked.size === entities.length ? "Deselect" : "Select") + " all"
                        }
                        className="-ml-3"
                        onClick={handleSelectAll}
                    />
                    {entities.map((entity) => (
                        <LayerCheckbox
                            key={entity.id}
                            isChecked={idsChecked.has(entity.id)}
                            onClick={() => {
                                onEntityClicked(entity, !idsChecked.has(entity.id))

                                setIdsChecked((prev) => {
                                    if (prev.has(entity.id)) prev.delete(entity.id)
                                    else prev.add(entity.id)
                                    return new Set(prev)
                                })
                            }}
                            title={entity.name}
                            errorMessage={entity.errorMessage}
                            isDisabled={!!entity.errorMessage}
                        />
                    ))}
                </div>
            </InspectorPanelAccordion>
        </div>
    )
}

const LayerCheckbox = ({
    isChecked,
    onClick,
    title,
    errorMessage,
    isDisabled,
}: {
    isChecked: boolean
    onClick: () => void
    title: string
    errorMessage?: string
    isDisabled?: boolean
}) => {
    return (
        <div
            className={classNames(
                "flex items-center gap-2 my-1 select-none w-full group group/tooltip relative",
                { "cursor-pointer": !isDisabled, "cursor-not-allowed": isDisabled }
            )}
            onClick={!isDisabled ? onClick : undefined}
        >
            <div className="flex-shrink-0">
                <Checkbox
                    isChecked={isChecked}
                    onClick={noop}
                    size="small"
                    isDisabled={isDisabled}
                />
            </div>
            <small
                className={classNames("truncate max-w-full", {
                    "text-gray-300": !isChecked,
                    "group-hover:text-gray-0": !isChecked && !isDisabled,
                    "text-gray-0": isChecked,
                })}
            >
                {title}
            </small>
            {errorMessage && <Tooltip title={errorMessage} />}
        </div>
    )
}
